import React from 'react';

/**
 * Enhanced Button Component with Loading State
 * Supports spinner, dots, or pulse loading indicators
 */
const LoadingButton = ({
  children,
  loading = false,
  disabled = false,
  onClick,
  type = 'button',
  variant = 'primary', // primary, secondary, danger, success
  size = 'md', // sm, md, lg
  loadingText = 'Loading...',
  loadingType = 'spinner', // spinner, dots, pulse
  className = '',
  icon,
  ...props
}) => {
  const baseClasses = 'font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.95] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 inline-flex items-center justify-center gap-2 btn-ripple relative overflow-hidden';
  
  const variantClasses = {
    primary: 'bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white',
    secondary: 'bg-gray-200 hover:bg-gray-300 text-gray-800',
    danger: 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white',
    success: 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white',
  };

  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
  };

  const isDisabled = disabled || loading;

  const renderLoadingIndicator = () => {
    if (!loading) return null;

    switch (loadingType) {
      case 'spinner':
        return (
          <div className="animate-spin rounded-full border-2 border-current border-t-transparent" style={{ width: '1em', height: '1em' }}></div>
        );
      case 'dots':
        return (
          <div className="flex gap-1">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-1.5 h-1.5 bg-current rounded-full animate-pulse"
                style={{ animationDelay: `${i * 0.2}s` }}
              ></div>
            ))}
          </div>
        );
      case 'pulse':
        return (
          <div className="w-4 h-4 bg-current rounded-full animate-pulse"></div>
        );
      default:
        return null;
    }
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      className={`
        ${baseClasses}
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${className}
        ${loading ? 'cursor-wait' : ''}
      `}
      {...props}
    >
      {loading ? (
        <>
          {renderLoadingIndicator()}
          {loadingText}
        </>
      ) : (
        <>
          {icon && <span>{icon}</span>}
          {children}
        </>
      )}
    </button>
  );
};

export default LoadingButton;

